/**
 * PATH: apps/quote/src/components/builder/panels/context-panel/FileItem.tsx
 * PURPOSE: Single file attachment row with actions (preview, download, pin, delete)
 */

import { useState } from 'react'
import {
  Eye, Download, MoreHorizontal, Pin, PinOff, Trash2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { type QuoteAttachment } from '@/lib/quoteApi'
import { getFileIcon, formatFileSize } from './helpers'

interface FileItemProps {
  file: QuoteAttachment
  quoteId: string
  isEditable: boolean
  onTogglePin: (file: QuoteAttachment) => void
  onDelete: (file: QuoteAttachment) => void
  onPreview: (file: QuoteAttachment) => void
  isPinning: boolean
  isDeleting: boolean
}

export default function FileItem({
  file,
  quoteId,
  isEditable,
  onTogglePin,
  onDelete,
  onPreview,
  isPinning,
  isDeleting,
}: FileItemProps) {
  const [showMenu, setShowMenu] = useState(false)

  const handleDownload = () => {
    if (file.file_url) {
      const a = document.createElement('a')
      a.href = file.file_url
      a.download = file.original_filename
      a.click()
    }
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors',
        file.is_pinned && 'ring-1 ring-blue-500/30'
      )}
    >
      <div className="p-1.5 bg-white dark:bg-slate-900 rounded">
        {getFileIcon(file.file_type, file.original_filename)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
            {file.title || file.original_filename}
          </p>
          {file.is_pinned && <Pin className="w-3 h-3 text-blue-500 shrink-0" />}
        </div>
        <p className="text-xs text-slate-500">
          {formatFileSize(file.file_size)}
          {file.uploaded_by_name && ` • ${file.uploaded_by_name}`}
        </p>
      </div>

      <div className="flex items-center gap-0.5 relative">
        <button
          onClick={() => onPreview(file)}
          className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
          title="Preview"
        >
          <Eye className="w-3.5 h-3.5 text-slate-500" />
        </button>
        <button
          onClick={handleDownload}
          className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
          title="Download"
        >
          <Download className="w-3.5 h-3.5 text-slate-500" />
        </button>

        {isEditable && (
          <>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
            >
              <MoreHorizontal className="w-3.5 h-3.5 text-slate-500" />
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-full mt-1 z-20 w-36 py-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700">
                  <button
                    onClick={() => { onTogglePin(file); setShowMenu(false) }}
                    disabled={isPinning}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
                  >
                    {file.is_pinned ? <><PinOff className="w-4 h-4" /> Unpin</> : <><Pin className="w-4 h-4" /> Pin</>}
                  </button>
                  <button
                    onClick={() => { onDelete(file); setShowMenu(false) }}
                    disabled={isDeleting}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
